import {
  BottomNavigation,
  BottomNavigationAction,
  BottomNavigationActionProps,
} from '@material-ui/core';
import SpeedDial from '@material-ui/lab/SpeedDial';
import SpeedDialAction from '@material-ui/lab/SpeedDialAction';
import SpeedDialIcon from '@material-ui/lab/SpeedDialIcon';
import ConsumerRoutePath from 'app/consumer-app/ConsumerRoutePath';
import Icon from 'app/shared/components/icon/Icon';
import IconLib, {
  RepairerSvg,
} from 'app/shared/components/icon/icon-lib';
import styledFuncs from 'app/styles/styledFuncs';
import styledMixins from 'app/styles/styledMixins';
import useSafeAreaInsets from 'app/tools/hooks/use-safe-area-insets';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components/macro';
import { FixedLengthArray, Merge } from 'type-fest';
import { appEnv, AppVariant } from 'app/core/environment';
import SharedRoutePath from '../SharedRoutePath';

export const ADD_LABEL = 'Add';
const isConsumer = appEnv.APP_VARIANT === AppVariant.CONSUMER;
const NavBar = styled(BottomNavigation)`
  height: 65px;
  .MuiBottomNavigationAction-label {
    line-height: 1.1;
    min-height: 1.8em;
    ${styledMixins.flexCenter}
  }
  .MuiBottomNavigationAction-root svg path {
    fill: rgba(0, 0, 0, 0.54);
  }
  .MuiBottomNavigationAction-root.Mui-selected svg path {
    fill: #31ad31;
  }
`;
const NotificationCount = styled.div`
  position: absolute;
  width: ${styledFuncs.pad(2)};
  height: ${styledFuncs.pad(2)};
  right: ${styledFuncs.pad(2.9)};
  bottom: ${styledFuncs.pad(4.3)};
  border-radius: 10px;
  background: #31ad31;
  text-align: center;
  color: white;
  z-index: 1;
  padding: 1px;
`;
const NotificationRepairsCount = styled.div`
  position: absolute;
  width: ${styledFuncs.pad(2)};
  height: ${styledFuncs.pad(2)};
  bottom: ${styledFuncs.pad(4.3)};
  border-radius: 10px;
  background: #31ad31;
  text-align: center;
  color: white;
  z-index: 1;
  padding: 1px;
`;
const TranslucentUnderlay: React.VFC<{
  open: boolean;
  color: 'green' | 'white';
}> = ({ open, color }) => (
  <div className="relative">
    <div
      style={{
        backgroundColor: 'rgb(0 0 0 / 41%)',
        position: open ? 'fixed' : 'absolute',
        top: 0,
        left: 0,
        right: 0,
      }}
      className={clsx(
        'w-full',
        open ? 'bottom-0' : '-bottom-20',
        color === 'green' ? 'from-primary' : 'from-white'
      )}
    />
  </div>
);

type NavActionProps = Merge<
  BottomNavigationActionProps,
  NavActionOpts
>;
const NavActionBase = ({ icon, data, ...props }: NavActionProps) => (
  <>
    {props.label === 'Notifications' && Number(data) > 0 && (
      <NotificationCount>{data}</NotificationCount>
    )}
    {props.label === 'Repairs' &&
      Number(data) > 0 &&
      (isConsumer ? (
        <NotificationRepairsCount style={{ left: '31%' }}>
          {data}
        </NotificationRepairsCount>
      ) : (
        <NotificationRepairsCount style={{ left: '38%' }}>
          {data}
        </NotificationRepairsCount>
      ))}
    {props.label !== 'Repairs' && (
      <BottomNavigationAction
        icon={<Icon className="mx-0 my-[4px]" {...{ icon }} />}
        {...props}
      />
    )}
    {props.label === 'Repairs' &&
      (isConsumer ? (
        <BottomNavigationAction
          icon={<Icon className="mx-0 my-[4px]" {...{ icon }} />}
          {...props}
        />
      ) : (
        <BottomNavigationAction
          icon={<RepairerSvg className="mx-0 my-[4px]" />}
          {...props}
        />
      ))}
  </>
);

export const NavAction = styled(NavActionBase)`
  min-width: 60px;

  .MuiBottomNavigationAction-label {
    transition: font-size 0.1s, opacity 0.1s;
    font-size: ${styledFuncs.pxToRem(10)};

    &.Mui-selected {
      font-size: ${styledFuncs.pxToRem(11)};
    }
  }
`;

const NavActionAdd: React.FC = ({ children }) => {
  const location = useLocation();
  const isHome = location.pathname === SharedRoutePath.HOME;
  return (
    <div className="flex relative z-10 flex-col-reverse flex-grow-0.5">
      {children}
      <p
        className={clsx(
          '-mx-1 text-center text-primary transition-all line-clamp-1 mb-[8px] text-[11px] h-[16px]',
          isHome || 'h-0'
        )}
      >
        Add Product
      </p>
    </div>
  );
};

const ButtonStack: React.VFC<{
  open: boolean;
  setOpen: (arg: boolean) => void;
}> = ({ open, setOpen }) => {
  const history = useHistory();
  const location = useLocation();
  const isHome = location.pathname === SharedRoutePath.HOME;
  // const impactRef = useRef();
  // const useOutsideClick = (ref: any) => {
  //   useEffect(() => {
  //     const handleClickOutside = evt => {
  //       if (ref && !ref?.contains(evt.target)) {
  //         setOpen(false);
  //       }
  //     };
  //     document.addEventListener('mousedown', handleClickOutside);
  //     return () => {
  //       document.removeEventListener('mousedown', handleClickOutside);
  //     };
  //   });
  // };
  // useOutsideClick(impactRef, () => setOpen(false));
  return (
    <SpeedDial
      // ref={impactRef}
      ariaLabel="add new product"
      icon={<SpeedDialIcon />}
      onClose={(...[, reason]) => {
        if (reason !== 'mouseLeave') setOpen(false);
        // if (reason === 'toggle') setOpen(false);
      }}
      onOpen={(...[, reason]) => {
        if (reason === 'toggle') setOpen(true);
      }}
      open={open}
      className={clsx(
        'absolute bottom-3.5 transition-all abs-center-x',
        isHome || 'bottom-1.5'
      )}
      classes={{ fab: clsx(isHome || 'w-4 h-4') }}
      css={`
        .MuiFab-sizeSmall {
          width: 45px;
          height: 45px;
        }
        .MuiSpeedDial-actions {
          margin-bottom: ${isHome ? -40 : -25}px;
          flex-direction: row-reverse;
          text-align: center;
          gap: 0 50px;
        }
        .MuiSpeedDialAction-staticTooltip {
          flex-direction: column-reverse;
        }
        .MuiSpeedDialAction-staticTooltipLabel {
          position: initial;
          background-color: transparent;
          box-shadow: none;
          color: white;
        }
      `}
    >
      {[
        {
          icon: IconLib.SCAN,
          tooltip: 'Scan barcode', // \u00A0 = no-breaking space Scan\u00A0barcode
          route: ConsumerRoutePath.PRODUCT_SCAN,
        },
        {
          icon: IconLib.SEARCH,
          tooltip: 'Search product', // \u00A0 = no-breaking space Search\u00A0product
          route: ConsumerRoutePath.PRODUCT_SEARCH_CATALOG,
        },
      ].map(({ icon, tooltip, route }) => (
        <SpeedDialAction
          key={tooltip}
          icon={<Icon {...{ icon }} color="#31ad31" />}
          tooltipTitle={tooltip}
          classes={{
            staticTooltipLabel: 'leading-4 mr-0.5 px-1 text-[15px]',
            fab: 'm-0.5',
          }}
          tooltipOpen
          aria-label={tooltip.replace('\u00A0', ' ').toLowerCase()}
          onClick={() => {
            history.push(route);
            setOpen(false);
          }}
        />
      ))}
    </SpeedDial>
  );
};

export type NavActionOpts = {
  label: string;
  value: string;
  icon: IconLib;
  data?: string;
};

export type NavActions = FixedLengthArray<NavActionOpts, 3 | 4 | 5>;

type Props = {
  navActions: NavActions | null;
};

export default function BottomNav({
  navActions,
}: Props): JSX.Element | null {
  const history = useHistory();
  const location = useLocation();
  const [activeRoute, setActiveRoute] = useState<string>(
    SharedRoutePath.HOME
  );

  const [speedDialOpen, setSpeedDialOpen] = useState(false);

  useEffect(() => {
    if (location.pathname !== activeRoute) {
      setActiveRoute(location.pathname);
    }
  }, [activeRoute, location.pathname]);

  const handleNavBarChange = (...[, newValue]: unknown[]) => {
    if (typeof newValue !== 'string') {
      throw new Error(
        `newValue is wrong type. Expected 'string', found ${typeof newValue}`
      );
    }
    setActiveRoute(newValue);
    history.push(newValue);
  };

  const { bottom: btm } = useSafeAreaInsets();
  return (
    navActions && (
      <>
        <TranslucentUnderlay open={speedDialOpen} color="white" />
        <TranslucentUnderlay open={speedDialOpen} color="green" />
        <div
          className="relative bg-white"
          style={{ paddingBottom: `${Math.max(0, btm * 0.6)}px` }}
        >
          <NavBar
            showLabels
            value={activeRoute}
            onChange={handleNavBarChange}
          >
            {navActions.map(action =>
              action.label === ADD_LABEL ? (
                <NavActionAdd key="add-stack-options">
                  <ButtonStack
                    open={speedDialOpen}
                    setOpen={setSpeedDialOpen}
                  />
                </NavActionAdd>
              ) : (
                <NavAction key={action.label} {...action} />
              )
            )}
          </NavBar>
        </div>
      </>
    )
  );
}
